import { SpanStatusCode } from '@opentelemetry/api';
import type { ZodError, ZodIssue } from 'zod';
import type { WideEventsWrapper } from './wide-events';

/**
 * Utility function to track a domain operation with automatic span management.
 *
 * This function creates a domain span and automatically handles success/error states.
 * Note: This is for tracking domain layer execution, not setting the main operation type.
 * Use setOperation() at the route level to set the main operation type.
 *
 * @param tracker - Wide events wrapper (optional)
 * @param aggregate - Domain aggregate name (e.g., 'user', 'order')
 * @param operation - Operation name (e.g., 'create', 'update')
 * @param fn - Function to execute within the domain operation
 * @returns Promise with the operation result
 *
 * @example
 * ```typescript
 * // At route level
 * tracker.setOperation('user.create', { domain: 'user-management', layer: 'api' });
 *
 * // In domain layer
 * const result = await withDomainTracking(tracker, 'user', 'create', async () => {
 *   // Domain logic here
 *   return await createUser(data);
 * });
 * ```
 */
export async function withDomainTracking<T>(
  tracker: WideEventsWrapper | undefined,
  aggregate: string,
  operation: string,
  fn: () => Promise<T>
): Promise<T> {
  if (!tracker) {
    return await fn();
  }

  const span = tracker.createOperationSpan(`domain.${aggregate}.${operation}`, {
    'operation.layer': 'domain',
    'domain.aggregate': aggregate,
    'domain.operation': operation,
  });

  try {
    const result = await fn();

    span.setStatus({ code: SpanStatusCode.OK });
    tracker.recordEvent(`domain.${aggregate}.${operation}.completed`, {
      aggregate,
      operation,
    });

    return result;
  } catch (error) {
    span.setStatus({
      code: SpanStatusCode.ERROR,
      message: error instanceof Error ? error.message : String(error),
    });

    if (error instanceof Error) {
      span.recordException(error);
    }

    tracker.recordEvent(`domain.${aggregate}.${operation}.failed`, {
      aggregate,
      operation,
      error: error instanceof Error ? error.message : String(error),
    });

    throw error;
  } finally {
    span.end();
  }
}

/**
 * Utility function to track event store operations with automatic span management.
 *
 * This function creates an event store span, tracks infrastructure details,
 * and automatically handles success/error states.
 *
 * @param tracker - Wide events wrapper (optional)
 * @param streamId - Event store stream ID
 * @param operation - Operation name (e.g., 'append', 'read')
 * @param fn - Function to execute within the event store operation
 * @returns Promise with the operation result
 *
 * @example
 * ```typescript
 * const result = await withEventStoreTracking(tracker, 'user-123', 'append', async () => {
 *   return await eventStore.append(streamId, events);
 * });
 * ```
 */
export async function withEventStoreTracking<T>(
  tracker: WideEventsWrapper | undefined,
  streamId: string,
  operation: string,
  fn: () => Promise<T>
): Promise<T> {
  if (!tracker) {
    return await fn();
  }

  const span = tracker.createOperationSpan(`event_store.${operation}`, {
    'operation.layer': 'event_store',
    'event_store.operation': operation,
    'event_store.stream_id': streamId,
  });

  tracker.trackInfrastructure({
    eventStore: {
      streamId,
    },
  });

  try {
    const result = await fn();

    span.setStatus({ code: SpanStatusCode.OK });
    tracker.recordEvent(`event_store.${operation}.completed`, {
      operation,
      stream_id: streamId,
    });

    return result;
  } catch (error) {
    span.setStatus({
      code: SpanStatusCode.ERROR,
      message: error instanceof Error ? error.message : String(error),
    });

    if (error instanceof Error) {
      span.recordException(error);
    }

    tracker.recordEvent(`event_store.${operation}.failed`, {
      operation,
      stream_id: streamId,
      error: error instanceof Error ? error.message : String(error),
    });

    throw error;
  } finally {
    span.end();
  }
}

/**
 * Utility function to track validation operations with automatic error handling and categorization.
 *
 * This function creates a validation span and automatically tracks validation
 * errors with appropriate context and categorization. Validation errors are
 * automatically categorized as expected errors (unexpected: false) since they
 * typically represent user input issues rather than system failures.
 *
 * **Error Categorization:**
 * - ValidationError/ZodError: Expected (unexpected: false) - user input issues
 * - Other errors: Unexpected (unexpected: true) - system/programming issues
 *
 * This is for tracking validation layer execution.
 * Use setOperation() at the route level to set the main operation type.
 *
 * @param tracker - Wide events wrapper (optional)
 * @param validationType - Type of validation (e.g., 'input', 'business', 'schema')
 * @param fn - Function to execute validation
 * @returns Promise with the validation result
 *
 * @example
 * ```typescript
 * // At route level
 * tracker.setOperation('user.create', { domain: 'user-management', layer: 'api' });
 *
 * // In validation layer
 * const validatedData = await withValidationTracking(tracker, 'input', async () => {
 *   return await validateUserInput(data);
 * });
 * ```
 */
export async function withValidationTracking<T>(
  tracker: WideEventsWrapper | undefined,
  validationType: string,
  fn: () => Promise<T>
): Promise<T> {
  if (!tracker) {
    return await fn();
  }

  const span = tracker.createOperationSpan(`validation.${validationType}`, {
    'operation.layer': 'validation',
    'validation.type': validationType,
  });

  try {
    const result = await fn();

    span.setStatus({ code: SpanStatusCode.OK });
    tracker.recordEvent(`validation.${validationType}.completed`, {
      validation_type: validationType,
    });

    return result;
  } catch (error) {
    span.setStatus({
      code: SpanStatusCode.ERROR,
      message: error instanceof Error ? error.message : String(error),
    });

    if (error instanceof Error) {
      span.recordException(error);
    }

    // Track validation error with appropriate context and categorization
    if (error instanceof Error && error.name === 'ZodError') {
      // Handle Zod validation errors - categorized as expected (user input issues)
      const zodError = error as ZodError;
      tracker.trackError({
        type: 'ValidationError',
        message: error.message,
        validation: {
          issueCount: zodError.issues?.length || 0,
          fields:
            zodError.issues?.map((issue: ZodIssue) => issue.path.join('.')) ||
            [],
        },
        unexpected: false, // Expected - user submitted invalid data
      });
    } else {
      // Handle other validation errors - categorized as unexpected (system issues)
      tracker.trackError({
        type: 'ValidationError',
        message: error instanceof Error ? error.message : String(error),
        validation: {
          issueCount: 1,
          fields: [],
        },
        unexpected: true, // Unexpected - likely a system/programming issue
      });
    }

    tracker.recordEvent(`validation.${validationType}.failed`, {
      validation_type: validationType,
      error: error instanceof Error ? error.message : String(error),
    });

    throw error;
  } finally {
    span.end();
  }
}

/**
 * Utility function to track external service calls with automatic timing and error handling.
 *
 * This function creates an external service span, tracks timing, and automatically
 * handles success/error states.
 *
 * @param tracker - Wide events wrapper (optional)
 * @param serviceName - Name of the external service
 * @param operation - Operation being performed on the service
 * @param fn - Function to execute the external call
 * @returns Promise with the external call result
 *
 * @example
 * ```typescript
 * const result = await withExternalServiceTracking(tracker, 'user-service', 'create', async () => {
 *   return await userService.createUser(data);
 * });
 * ```
 */
export async function withExternalServiceTracking<T>(
  tracker: WideEventsWrapper | undefined,
  serviceName: string,
  operation: string,
  fn: () => Promise<T>
): Promise<T> {
  if (!tracker) {
    return await fn();
  }

  const startTime = Date.now();
  const span = tracker.createOperationSpan(
    `external_service.${serviceName}.${operation}`,
    {
      'operation.layer': 'external_service',
      'external_service.name': serviceName,
      'external_service.operation': operation,
    }
  );

  tracker.trackInfrastructure({
    externalService: {
      name: serviceName,
      operation,
    },
  });

  try {
    const result = await fn();
    const duration = Date.now() - startTime;

    span.setStatus({ code: SpanStatusCode.OK });
    span.setAttributes({
      'external_service.duration_ms': duration,
    });

    tracker.trackInfrastructure({
      externalService: {
        name: serviceName,
        operation,
        duration,
      },
    });

    tracker.recordEvent(
      `external_service.${serviceName}.${operation}.completed`,
      {
        service_name: serviceName,
        operation,
        duration_ms: duration,
      }
    );

    return result;
  } catch (error) {
    const duration = Date.now() - startTime;

    span.setStatus({
      code: SpanStatusCode.ERROR,
      message: error instanceof Error ? error.message : String(error),
    });

    span.setAttributes({
      'external_service.duration_ms': duration,
    });

    if (error instanceof Error) {
      span.recordException(error);
    }

    tracker.trackInfrastructure({
      externalService: {
        name: serviceName,
        operation,
        duration,
      },
    });

    tracker.recordEvent(`external_service.${serviceName}.${operation}.failed`, {
      service_name: serviceName,
      operation,
      duration_ms: duration,
      error: error instanceof Error ? error.message : String(error),
    });

    throw error;
  } finally {
    span.end();
  }
}

/**
 * Utility function to track database operations with automatic timing and error handling.
 *
 * This function creates a database span, tracks timing and query details,
 * and automatically handles success/error states.
 *
 * @param tracker - Wide events wrapper (optional)
 * @param operation - Database operation (e.g., 'SELECT', 'INSERT', 'UPDATE')
 * @param table - Database table name
 * @param fn - Function to execute the database operation
 * @returns Promise with the database operation result
 *
 * @example
 * ```typescript
 * const result = await withDatabaseTracking(tracker, 'INSERT', 'users', async () => {
 *   return await db.insert(userData);
 * });
 * ```
 */
export async function withDatabaseTracking<T>(
  tracker: WideEventsWrapper | undefined,
  operation: string,
  table: string,
  fn: () => Promise<T>
): Promise<T> {
  if (!tracker) {
    return await fn();
  }

  const startTime = Date.now();
  const span = tracker.createOperationSpan(
    `database.${operation.toLowerCase()}`,
    {
      'operation.layer': 'database',
      'db.operation': operation,
      'db.table': table,
    }
  );

  tracker.trackInfrastructure({
    database: {
      operation,
      table,
    },
  });

  try {
    const result = await fn();
    const duration = Date.now() - startTime;

    span.setStatus({ code: SpanStatusCode.OK });
    span.setAttributes({
      'db.duration_ms': duration,
    });

    tracker.trackInfrastructure({
      database: {
        operation,
        table,
        duration,
      },
    });

    tracker.recordEvent(`database.${operation.toLowerCase()}.completed`, {
      operation,
      table,
      duration_ms: duration,
    });

    return result;
  } catch (error) {
    const duration = Date.now() - startTime;

    span.setStatus({
      code: SpanStatusCode.ERROR,
      message: error instanceof Error ? error.message : String(error),
    });

    span.setAttributes({
      'db.duration_ms': duration,
    });

    if (error instanceof Error) {
      span.recordException(error);
    }

    tracker.trackInfrastructure({
      database: {
        operation,
        table,
        duration,
      },
    });

    tracker.recordEvent(`database.${operation.toLowerCase()}.failed`, {
      operation,
      table,
      duration_ms: duration,
      error: error instanceof Error ? error.message : String(error),
    });

    throw error;
  } finally {
    span.end();
  }
}

/**
 * Utility function to automatically track user context from various sources.
 *
 * This function attempts to extract user information from common sources
 * and automatically tracks it with the wide events wrapper.
 *
 * @param tracker - Wide events wrapper
 * @param sources - Object containing potential user data sources
 * @returns The tracker for method chaining
 *
 * @example
 * ```typescript
 * trackUserFromSources(tracker, {
 *   request: c.req.raw, // HTTP request
 *   body: requestBody,  // Request body
 *   headers: customHeaders, // Custom headers
 * });
 * ```
 */
export function trackUserFromSources(
  tracker: WideEventsWrapper,
  sources: {
    request?: Request;
    // biome-ignore lint/suspicious/noExplicitAny: userData is a dynamic object that can contain various user properties from different sources
    body?: any;
    headers?: Record<string, string>;
    // biome-ignore lint/suspicious/noExplicitAny: userData is a dynamic object that can contain various user properties from different sources
    context?: any;
  }
): WideEventsWrapper {
  // biome-ignore lint/suspicious/noExplicitAny: userData is a dynamic object that can contain various user properties from different sources
  const userData: any = {};

  // Extract from request headers
  if (sources.request) {
    const authHeader = sources.request.headers.get('authorization');
    if (authHeader) {
      if (authHeader.startsWith('Bearer ')) {
        userData.actorType = 'authenticated_user';
      } else if (authHeader.startsWith('Basic ')) {
        userData.actorType = 'basic_auth_user';
      }
    }

    const apiKey = sources.request.headers.get('x-api-key');
    if (apiKey) {
      userData.actorType = 'api_key_user';
    }
  }

  // Extract from request body
  if (sources.body) {
    if (sources.body.email) userData.email = sources.body.email;
    if (sources.body.firstName) userData.firstName = sources.body.firstName;
    if (sources.body.lastName) userData.lastName = sources.body.lastName;
    if (sources.body.userId) userData.id = sources.body.userId;
  }

  // Extract from custom headers
  if (sources.headers) {
    if (sources.headers['x-user-id'])
      userData.id = sources.headers['x-user-id'];
    if (sources.headers['x-user-email'])
      userData.email = sources.headers['x-user-email'];
  }

  // Extract from context
  if (sources.context) {
    if (sources.context.user) {
      Object.assign(userData, sources.context.user);
    }
  }

  // Set default actor type if not determined
  if (!userData.actorType) {
    userData.actorType = 'public';
  }

  return tracker.trackUser(userData);
}

/**
 * Utility function to create a simple operation tracker with common defaults.
 *
 * This function creates a tracker with sensible defaults for common operations
 * and automatically sets the operation type using the new setOperation() API.
 * The tracker will automatically categorize errors appropriately when they occur.
 *
 * @param entity - Entity type (e.g., 'user', 'order')
 * @param operation - Operation name (e.g., 'create', 'update')
 * @param serviceName - Service name (optional)
 * @returns Configured wide events wrapper with operation already set
 *
 * @example
 * ```typescript
 * const tracker = createSimpleTracker('user', 'create');
 * // Operation type is automatically set to 'user.create'
 * // Error categorization is automatic when errors occur
 * ```
 */
export function createSimpleTracker(
  entity: string,
  operation: string,
  serviceName?: string
): WideEventsWrapper {
  const { WideEventsWrapper } = require('./wide-events');

  const tracker = new WideEventsWrapper(
    `${entity} ${operation}`,
    `${entity.toLowerCase()}.${operation.toLowerCase()}`,
    serviceName
  );

  // Set the operation using the new API
  tracker.setOperation(`${entity.toLowerCase()}.${operation.toLowerCase()}`);

  return tracker;
}
